import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import AnimatedCounter from "../components/AnimatedCounter";
import {
  Sparkles, Award, Rocket, Target, Globe, TrendingUp,
  Users, Cpu, Handshake, ArrowRight, DollarSign, Zap,
  LineChart, Trophy, Building2, HeartHandshake,
} from "lucide-react";
import { stats } from "../lib/mockData";
import { bigImpact, futureVisionMilestones, problemStats } from "../lib/storyData";

const Slide = ({ number, label, children, accent = "primary" }) => {
  const accentBg = accent === "secondary" ? "bg-secondary text-secondary-foreground" : "bg-primary text-primary-foreground";
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-24 border-b border-border/40 last:border-b-0">
      <div className="flex items-center gap-3 mb-10">
        <div className={`h-10 w-10 rounded-2xl flex items-center justify-center font-display font-bold ${accentBg}`}>
          {number}
        </div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      </div>
      {children}
    </section>
  );
};

export default function Pitch() {
  const navigate = useNavigate();

  return (
    <div className="noise-bg">
      {/* Cover slide */}
      <section className="min-h-[85vh] flex items-center relative overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-0 -right-32 w-[500px] h-[500px] rounded-full bg-secondary/20 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase mb-8">
              <Trophy size={12} /> Investor & Judges Deck · 2026
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.95] text-balance max-w-5xl">
              The company building the <span className="text-primary">social infrastructure</span> of the next century.
            </h1>
            <p className="mt-8 text-xl text-muted-foreground max-w-2xl leading-relaxed">
              NeighborNet AI — a Series-Seed opportunity to power humanity's largest self-healing network.
            </p>
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl">
              {[
                { label: "Communities", value: 620, suffix: "" },
                { label: "Countries", value: stats.countries, suffix: "" },
                { label: "Neighbors helped", value: stats.neighborsHelped, suffix: "+" },
                { label: "MoM growth", value: 47, suffix: "%" },
              ].map((k, i) => (
                <div key={k.label} data-testid={`pitch-cover-stat-${i}`}>
                  <div className="text-3xl sm:text-4xl font-display font-bold text-foreground">
                    <AnimatedCounter end={k.value} suffix={k.suffix} duration={1600} />
                  </div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{k.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 01 Problem */}
      <Slide number="01" label="The problem" accent="primary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          The world's biggest safety net has <span className="text-destructive">no operating system</span>.
        </h2>
        <p className="mt-6 text-lg text-muted-foreground max-w-3xl leading-relaxed">
          People helping people is the largest untapped economy on earth. It's fragmented across WhatsApp groups, church halls, GoFundMe, and word-of-mouth. Zero infrastructure. Zero scale. Zero AI.
        </p>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {problemStats.map((p, i) => (
            <div key={p.label} data-testid={`pitch-problem-${i}`} className={`fade-up stagger-${i + 1} rounded-3xl border border-border/60 bg-white p-8`}>
              <div className="text-5xl font-display font-bold text-primary">
                <AnimatedCounter end={p.value} suffix={p.suffix} duration={1500} />
              </div>
              <div className="mt-2 font-display font-semibold">{p.label}</div>
              <div className="text-sm text-muted-foreground mt-2">{p.desc}</div>
            </div>
          ))}
        </div>
      </Slide>

      {/* 02 Solution */}
      <Slide number="02" label="The solution" accent="secondary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          A <span className="text-secondary">mesh network</span> for kindness, powered by AI.
        </h2>
        <div className="grid lg:grid-cols-2 gap-10 items-center mt-12">
          <div>
            {[
              { icon: Users, t: "The graph", d: "Every neighbor, org, and cause is a node. Trust and reciprocity are edges." },
              { icon: Cpu, t: "The engine", d: "Proprietary AI matches needs to offers by proximity, urgency, skill, availability, and history." },
              { icon: Handshake, t: "The moat", d: "Local trust data compounds. The longer a community is on the platform, the more valuable it becomes." },
            ].map((s, i) => (
              <div key={s.t} className={`fade-up stagger-${i + 1} flex gap-4 mb-6`}>
                <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <s.icon size={22} />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-xl">{s.t}</h3>
                  <p className="mt-1 text-muted-foreground leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-secondary/40 blur-3xl" />
            <div className="relative">
              <div className="text-6xl font-display font-bold">
                <AnimatedCounter end={96} suffix="%" duration={1600} />
              </div>
              <div className="mt-2 text-lg text-primary-foreground/90">match confidence</div>
              <div className="mt-6 h-px bg-white/20" />
              <div className="mt-6 text-6xl font-display font-bold">
                <AnimatedCounter end={4} suffix=" min" duration={1600} />
              </div>
              <div className="mt-2 text-lg text-primary-foreground/90">median time to first help</div>
              <div className="mt-6 h-px bg-white/20" />
              <div className="mt-6 text-6xl font-display font-bold">
                <AnimatedCounter end={92} suffix="%" duration={1600} />
              </div>
              <div className="mt-2 text-lg text-primary-foreground/90">30-day retention</div>
            </div>
          </div>
        </div>
      </Slide>

      {/* 03 Market */}
      <Slide number="03" label="Market opportunity" accent="primary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          A <span className="text-primary">$4T</span> annual mutual-aid economy. Zero incumbents.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { size: "$4T", label: "TAM · Global mutual aid + informal economy", color: "bg-primary text-primary-foreground" },
            { size: "$420B", label: "SAM · Digital-first communities in 60 countries", color: "bg-secondary text-secondary-foreground" },
            { size: "$18B", label: "SOM · 3-year serviceable market", color: "bg-white border border-border text-foreground" },
          ].map((m, i) => (
            <div key={m.label} data-testid={`pitch-market-${i}`} className={`fade-up stagger-${i + 1} rounded-3xl p-10 ${m.color}`}>
              <div className="text-6xl font-display font-bold tracking-tight">{m.size}</div>
              <div className={`mt-3 text-sm ${m.color.includes('bg-white') ? 'text-muted-foreground' : 'opacity-90'}`}>{m.label}</div>
            </div>
          ))}
        </div>
        <p className="mt-10 text-muted-foreground max-w-2xl leading-relaxed">
          Category creation opportunity: no VC-backed player owns the intersection of hyper-local + AI matching + humanitarian utility.
        </p>
      </Slide>

      {/* 04 Traction */}
      <Slide number="04" label="Traction" accent="secondary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          Hockey-stick growth with <span className="text-secondary">zero paid marketing</span>.
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mt-12">
          {bigImpact.map((b, i) => (
            <div
              key={b.key}
              data-testid={`pitch-traction-${b.key}`}
              className={`fade-up stagger-${i + 1} rounded-3xl bg-white border border-border/60 p-6`}
            >
              <div className="text-3xl sm:text-4xl font-display font-bold text-primary tracking-tight">
                <AnimatedCounter end={b.end} suffix={b.suffix} duration={1800} />
              </div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-2">{b.label}</div>
            </div>
          ))}
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: TrendingUp, t: "47% MoM growth", d: "Users, matches and communities all compounding weekly." },
            { icon: HeartHandshake, t: "68 NPS", d: "Users tell 3.2 friends within 30 days on average." },
            { icon: LineChart, t: "$0 CAC", d: "100% organic. Communities onboard entire blocks in a week." },
          ].map((m, i) => (
            <div key={m.t} className={`fade-up stagger-${i + 1} rounded-2xl bg-secondary/10 p-6`}>
              <m.icon size={20} className="text-secondary" />
              <div className="mt-3 font-display font-semibold text-lg">{m.t}</div>
              <div className="text-sm text-muted-foreground mt-1">{m.d}</div>
            </div>
          ))}
        </div>
      </Slide>

      {/* 05 Business model */}
      <Slide number="05" label="Business model" accent="primary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          Free for neighbors. <span className="text-primary">Paid</span> by the systems that need us.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            {
              icon: Building2,
              title: "City & NGO partnerships",
              price: "$5-50k / yr per city",
              d: "Municipal governments and NGOs pay for community intelligence dashboards, program placement and outcome reporting.",
            },
            {
              icon: DollarSign,
              title: "Impact-as-a-Service",
              price: "1-3% of donation volume",
              d: "Corporate CSR and giving Tuesday partners route matched giving through the platform.",
            },
            {
              icon: Zap,
              title: "Employer benefits",
              price: "$4 / employee / mo",
              d: "Fortune 5000 companies offer NeighborNet as a wellbeing benefit — turnkey volunteering, meals, mentorship.",
            },
          ].map((b, i) => (
            <div key={b.title} data-testid={`pitch-model-${i}`} className={`fade-up stagger-${i + 1} rounded-3xl border border-border/60 bg-white p-8`}>
              <div className="h-11 w-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                <b.icon size={20} />
              </div>
              <h3 className="font-display font-semibold text-xl">{b.title}</h3>
              <div className="mt-2 text-sm font-semibold text-secondary">{b.price}</div>
              <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>
      </Slide>

      {/* 06 Vision / Roadmap */}
      <Slide number="06" label="Vision" accent="secondary">
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl">
          From a block to the whole <span className="text-secondary">planet</span>.
        </h2>
        <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed text-lg">
          Our five-year path to becoming default social infrastructure — the plumbing of how humanity helps itself.
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {futureVisionMilestones.map((m, i) => (
            <div key={m.year} data-testid={`pitch-vision-${m.year}`} className={`fade-up stagger-${i + 1} rounded-3xl bg-white border border-border/60 p-6 relative`}>
              <div className="absolute top-6 right-6 text-primary/30 font-display font-bold text-4xl">{i + 1}</div>
              <div className="text-2xl font-display font-bold text-primary">{m.year}</div>
              <div className="mt-2 font-display font-semibold">{m.title}</div>
              <div className="mt-2 text-sm text-muted-foreground">{m.desc}</div>
            </div>
          ))}
        </div>
      </Slide>

      {/* 07 Founder & Vision */}
      <Slide number="07" label="Founder & vision" accent="primary">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance">
              Built by a founder who believes <span className="text-primary">technology should serve humanity</span>.
            </h2>

            <div className="mt-10 rounded-3xl bg-white border border-border/60 p-8">
              <div className="flex items-start gap-5 mb-2">
                <div
                  data-testid="founder-headshot"
                  aria-label="Founder Adebowale Jimoh"
                  className="h-20 w-20 shrink-0 rounded-2xl bg-gradient-to-br from-primary to-secondary text-white font-display font-bold text-3xl flex items-center justify-center shadow-md ring-4 ring-primary/10"
                >
                  AJ
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">
                    Founder
                  </div>
                  <div className="font-display text-3xl sm:text-4xl font-bold text-foreground tracking-tight leading-tight">
                    Adebowale Jimoh
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">Founder · NeighborNet AI</div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-4">
                  Vision
                </div>
                <blockquote className="relative">
                  <Sparkles size={22} className="text-primary/40 mb-3" />
                  <p className="font-display text-xl sm:text-2xl leading-relaxed text-foreground italic text-balance">
                    "NeighborNet AI was created to empower communities to solve problems collectively through technology, compassion, and shared responsibility."
                  </p>
                </blockquote>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-[2rem] bg-gradient-to-br from-primary to-primary/70 text-primary-foreground p-10 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-secondary/30 blur-3xl" />
              <div className="relative space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-primary-foreground/70 font-semibold mb-2">
                    Guiding principles
                  </div>
                  <h3 className="font-display text-2xl font-bold">Three commitments</h3>
                </div>
                {[
                  { t: "Technology", d: "AI as amplifier of human kindness, never a replacement for it." },
                  { t: "Compassion", d: "Every decision starts with the neighbor who needs help most." },
                  { t: "Shared responsibility", d: "The strongest communities own their outcomes together." },
                ].map((p, i) => (
                  <div key={p.t} className={`fade-up stagger-${i + 1} pt-6 border-t border-white/15`}>
                    <div className="font-display font-semibold text-lg">{p.t}</div>
                    <div className="text-sm text-primary-foreground/85 mt-1 leading-relaxed">{p.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* 08 The Ask */}
      <Slide number="08" label="The ask" accent="secondary">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-balance">
              Raising <span className="text-primary">$8M</span> Seed.
            </h2>
            <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
              To scale from 620 to 10,000 communities in 24 months. To turn kindness into infrastructure.
            </p>
            <div className="mt-8 space-y-3">
              {[
                { p: "40%", t: "AI engine + platform engineering" },
                { p: "30%", t: "Community expansion (25 new metros)" },
                { p: "20%", t: "Partnerships (cities, NGOs, employers)" },
                { p: "10%", t: "Trust, safety, and verification infra" },
              ].map((a, i) => (
                <div key={a.t} className={`fade-up stagger-${i + 1} flex items-center gap-4 rounded-2xl bg-white border border-border/60 p-4`}>
                  <div className="text-2xl font-display font-bold text-primary w-16">{a.p}</div>
                  <div className="text-sm">{a.t}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-[2rem] bg-gradient-to-br from-secondary to-secondary/70 text-secondary-foreground p-10 relative overflow-hidden">
            <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-primary/30 blur-3xl" />
            <div className="relative">
              <Target size={32} className="mb-6" />
              <h3 className="font-display text-3xl font-bold leading-tight">
                Join us in building the operating system for the next century of human cooperation.
              </h3>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  onClick={() => navigate("/signup")}
                  data-testid="pitch-signup-btn"
                  className="rounded-full bg-white text-secondary hover:bg-white/90 h-12 px-6"
                >
                  Start on the platform
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/stats")}
                  data-testid="pitch-stats-btn"
                  className="rounded-full h-12 px-6 border-white/30 text-white bg-transparent hover:bg-white/10"
                >
                  See live impact
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* Closing */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-24 text-center">
        <Sparkles className="mx-auto text-secondary mb-6" size={40} />
        <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05] text-balance max-w-4xl mx-auto">
          "Never doubt that a small group of thoughtful, committed citizens can change the world."
        </h2>
        <div className="mt-6 text-muted-foreground">— Margaret Mead</div>
        <div className="mt-10">
          <Button
            onClick={() => navigate("/")}
            data-testid="pitch-back-home"
            variant="outline"
            className="rounded-full h-12 px-7 border-border"
          >
            <ArrowRight className="mr-1 rotate-180" size={16} /> Back to home
          </Button>
        </div>
      </section>
    </div>
  );
}
